import {
  ArpeggiCompositionV5,
  BeatResolution,
  CURRENT_SONG_VERSION,
  deserializeSongSnapshot,
  GenesisSampleType,
  hasComposition,
  KeyAndScale,
  MAX_BARS_COUNT,
  MAX_STEM_COUNT,
  migrateSongToLatest,
  Scale,
  serializeSongSnapshot,
  StemType,
  TICKS_PER_LOOP
} from '@arpeggi/shared'
import {
  clone,
  getSnapshot,
  Instance,
  onPatch,
  SnapshotIn,
  SnapshotOut,
  types
} from 'mobx-state-tree'
import { IArpMedia } from '../../../features/arp-registry/ArpMedia'
import { ForDirectExtend } from '../../../features/mobx'
import { SongInternal } from '../../client/genesis/genesis_proto_pb'
import { Arp2Utils } from '../../client/SongUtils'
import { ticksToSeconds } from '../../components/studio/StudioUtils'
import { SongGenerator } from '../../generator/SongGenerator'
import { LOOP_COLORS } from '../../lib/stitches'
import { findKey } from '../../mint/KeyFinder'
import { log, LogCategories } from '../../utils/logger'
import { IClipStore } from './clip/ClipStore'
import { ILoopRefStore } from './loop/LoopRefStore'
import { ISampleWithEffectsStore } from './sample/SampleWithEffectsStore'
import { AudioStemStore, IAudioStemStore } from './stem/AudioStemStore'
import { DrumStemStore, IDrumStemStore } from './stem/DrumStemStore'
import { IInstrumentStemStore, InstrumentStemStore } from './stem/InstrumentStemStore'
import { IStemStore } from './StemStore'

export const SongStoreWithoutProcessor = types
  .model({
    // We store current Song Version from StemsStore to keep these in-sync.
    version: CURRENT_SONG_VERSION,
    tempo: 160,
    tokenColor: types.maybeNull(types.string),
    stems: types.array(
      types.union(
        {
          dispatcher: (snapshot) => {
            switch (snapshot.stemType) {
              case StemType.INSTRUMENT:
                return InstrumentStemStore
              case StemType.DRUMS:
                return DrumStemStore
              case StemType.AUDIO:
                return AudioStemStore
            }
          },
          eager: true
        },
        InstrumentStemStore,
        DrumStemStore,
        AudioStemStore
      )
    ),
    name: ''
    // mixer: types.optional(MixerStore)
  })
  .views((self) => ({
    // This is how much of the song to display in the arrangement view, including empty buffer loops at the end of the song.
    get songDisplayLength(): number {
      let maxLength = 0
      self.stems.forEach((stem, index) => {
        maxLength = Math.max(maxLength, stem.stemLength)
      })
      return Math.min(Math.max(maxLength, 16), MAX_BARS_COUNT)
    },
    // This is the actual length of the longest populated stem in the song.
    get songRealLength(): number {
      // Math.max.apply gets max of whole array
      return Math.max.apply(
        null,
        self.stems.map((stem) => stem.stemRealLength)
      )
    },
    // gets the length of the song's mint output in seconds
    // this will take into account if the last bar contains only a clip,
    // and if so, will cut off at the end of the last clip
    get songMintLengthSeconds(): number {
      let songLengthTicks = 0
      self.stems.forEach((stem) => {
        if (stem.type() === StemType.INSTRUMENT || stem.type() === StemType.DRUMS) {
          songLengthTicks = Math.max(songLengthTicks, stem.stemRealLength * TICKS_PER_LOOP)
        } else if (stem.type() === StemType.AUDIO) {
          // deliberately casting as any to not cause a circular dependency
          const audioStem = stem as any // IAudioStemStore
          songLengthTicks = Math.max(songLengthTicks, audioStem.stemEndingInTicks)
        }
      })
      const songLengthSeconds = ticksToSeconds(songLengthTicks, self.tempo)
      return songLengthSeconds
    }
  }))
  .views((self) => ({
    // gets the length of the song's mint output in 16th notes
    // this will take into account if the last bar contains only a clip,
    // and if so, will cut off at the end of the last clip
    get songMintLength16Notes(): number {
      return Math.ceil((64 * self.songMintLengthSeconds) / 16 / (60 / self.tempo))
    },
    isReady() {
      return self.stems.length > 0
    },
    getAllLoopsAtSection(section: number): ILoopRefStore[] {
      return self.stems
        .filter((stem) => {
          return stem.getLoopAtSectionOrNull(section) !== null
        })
        .map((stem) => {
          return stem.getLoopAtSectionOrNull(section)
        })
    },
    getAllStemIdsAtSection(section: number): number[] {
      let stemIds = []
      self.stems.forEach((stem, stemId) => {
        if (stem.getLoopAtSectionOrNull(section) !== null) {
          stemIds.push(stemId)
        }
      })
      return stemIds
    },
    getStemByID(stemUniqueID: string): IStemStore | null {
      const stem = self.stems.find((stem) => stem.uniqueId === stemUniqueID)
      return stem ? stem : null
    },
    getStemIndexByID(stemUniqueID: string): number | null {
      let returnIndex = -1
      self.stems.forEach((stem, stemIndex: number) => {
        if (stem.uniqueId === stemUniqueID) returnIndex = stemIndex
      })
      return returnIndex > -1 ? returnIndex : null
    },
    getStemIDByIndex(stemIndex: number): string | null {
      if (stemIndex < 0 || stemIndex > self.stems.length - 1) return null
      return self.stems[stemIndex].uniqueId
    },
    getClipByID(clipID: string): IClipStore | null {
      let returnClip = null
      self.stems
        .filter((stem) => stem.type() === StemType.AUDIO)
        .forEach((audioStem: IAudioStemStore) => {
          const foundClip = audioStem.clips.find((clip) => clip.uniqueId === clipID)
          if (foundClip) returnClip = foundClip
          return
        })
      return returnClip
    },
    getClipParentStemIndex(clipID: string): number | null {
      let returnIndex = -1
      self.stems.forEach((stem, stemIndex: number) => {
        if (stem.type() !== StemType.AUDIO) return
        const audioStem = stem as IAudioStemStore
        if (audioStem.getClipByID(clipID)) {
          returnIndex = stemIndex
        }
      })
      return returnIndex > -1 ? returnIndex : null
    },
    getLoopIndex(stemIndex: number, section: number) {
      return self.stems[stemIndex].getLoopIndex(section)
    },
    get currentSamples(): number[] {
      return [
        ...new Set(
          self.stems.flatMap((stem: IStemStore) => {
            let sample
            switch (stem.type()) {
              case StemType.INSTRUMENT:
                sample = (stem as IInstrumentStemStore).melodicSample.sampleIndex
                break
              case StemType.DRUMS:
                sample = (stem as IDrumStemStore).drumSamples.map(
                  (drumSample) => drumSample.sampleIndex
                )
                break
              case StemType.AUDIO:
                sample = (stem as IAudioStemStore).clips
                  .map((clip) => clip.sampleWithEffects.sampleIndex)
                  .filter((index) => index > -1)
                break
            }
            if (sample <= 0) {
              log.warn(
                { category: LogCategories.MOBX, data: { sample } },
                'Warning: current song contains an invalid sample. The song may have been deserialized incorrectly.'
              )
            }
            return sample
          })
        )
      ]
    },
    /**
     * returns an array of all of the stem indexes we need to retrieve audio buffers for -- specifically used
     * for audio tracks that contain stems as clips (using their data URIs) within them
     */
    get currentRemixedAudioStems(): number[] {
      return self.stems.flatMap((stem: IStemStore) => {
        let samples = []
        if (stem.type() === StemType.AUDIO) {
          samples = (stem as IAudioStemStore).clips
            .filter((clip) => clip.sampleWithEffects.mediaType === 'stem')
            .map((clip) => clip.sampleWithEffects.sampleIndex)
        }
        if (samples.includes(0) || samples.includes(-1)) {
          log.warn(
            { category: LogCategories.MOBX, data: { samples } },
            'Warning: current song contains an invalid sample. The song may have been deserialized incorrectly.'
          )
        }
        return samples
      })
    },
    get lengthIn16thNotes() {
      return 64 * self.songRealLength
    },
    get lengthInSeconds() {
      if (self.stems.length === 0) return 0
      return (60 / self.tempo) * (self.songRealLength * 16)
    },
    get key(): KeyAndScale {
      return findKey(this)
    },
    get songLengthIn48n(): number {
      return self.songDisplayLength * TICKS_PER_LOOP
    },
    // returns true if at least one of the stems is a drum stem
    get hasDrumKit(): boolean {
      for (let i = 0; i < self.stems.length; i++) {
        if (self.stems[i].type() === StemType.DRUMS) return true
      }
      return false
    }
  }))
  .actions((self) => {
    function afterCreate() {
      self.stems.forEach((stem, i) => assignColorIndex(stem, i))
    }

    onPatch(self, (patch) => {
      if (patch.path.includes('stems')) {
        if (self.tokenColor) {
          self.stems.forEach(
            (stem) =>
              stem.setTokenColor &&
              stem.tokenColor !== self.tokenColor &&
              stem.setTokenColor(self.tokenColor)
          )
        }
      }
    })

    function assignColorIndex(stem: IStemStore, index?: number) {
      if (stem.colorIndex !== -1) return
      if (index === 0 || self.stems.length === 0)
        return stem.updateColorIndex(Math.floor(Math.random() * LOOP_COLORS.length))
      const newIndex = index || self.stems.length
      const firstColorIndex = newIndex + (self.stems[0]?.colorIndex || 0)
      return stem.updateColorIndex(firstColorIndex)
    }

    function addInstrumentStem(sample: ISampleWithEffectsStore) {
      if (self.stems.length >= MAX_STEM_COUNT) return

      const instrumentStemStore = InstrumentStemStore.create()
      instrumentStemStore.init(self.songDisplayLength, sample)
      assignColorIndex(instrumentStemStore)
      self.stems.push(instrumentStemStore)
    }

    function addAudioStem(sample?: ISampleWithEffectsStore) {
      if (self.stems.length >= MAX_STEM_COUNT) return

      const audioStemStore = AudioStemStore.create()
      audioStemStore.init(self.songDisplayLength, sample, self.tempo)
      assignColorIndex(audioStemStore)
      self.stems.push(audioStemStore)
    }

    function addDrumStem(sample: ISampleWithEffectsStore = null) {
      if (self.stems.length >= MAX_STEM_COUNT) return

      const drums = DrumStemStore.create()
      drums.init(self.songDisplayLength, [])
      assignColorIndex(drums)
      self.stems.push(drums)
      if (sample) {
        addDrumSampleToStem(sample, self.stems.length - 1)
      }
    }

    function addDrumSampleToStem(sample: ISampleWithEffectsStore, index: number) {
      getStem(index).addDrumSample(sample)
    }

    function addStemFromRemix(
      stem: IArpMedia,
      setArrangementABC: boolean = true,
      autoTranspose: boolean = true
    ) {
      if (self.stems.length >= MAX_STEM_COUNT) return

      // Handle the case for remixing stems that don't have composition.
      if (!hasComposition(stem.asInternalArpMedia())) {
        const stemSample = Arp2Utils.createSampleWithEffects(stem.id, stem.category)
        stemSample.updateMediaType('stem')
        addAudioStem(stemSample)
        return
      }

      const stemStore: IStemStore = clone(stem.getStemStore())
      const songKey: KeyAndScale = self.key
      const stemKey = new KeyAndScale()
      stemKey.fromInt(stem.metadata.key)

      if (autoTranspose) {
        const difference = stemKey.differenceTo(songKey)
        stemStore.transposeBy(difference)
      }

      // we need to change this to the chromatic scale so that all notes appear in the piano roll
      if (stemStore.type() === StemType.INSTRUMENT) {
        stemStore.loops.forEach((loop) => loop.melodicLoop.updateScale(Scale.CHROMATIC))
      }

      // Change arrangement to be A, B, C ...
      if (setArrangementABC) {
        stemStore.setArrangementABC()
      }

      stemStore.updateRemixProperties(stem.user.displayName, stem.metadata.title)
      assignColorIndex(stemStore)
      self.stems.push(stemStore)
    }

    function removeStem(stemIndex: number) {
      self.stems.splice(stemIndex, 1)
    }

    function changeStemIndex(currentStemIndex: number, newStemIndex: number) {
      if (currentStemIndex === newStemIndex) return
      const valueAtCurrent = getSnapshot(self.stems.splice(currentStemIndex, 1)[0])
      self.stems.splice(newStemIndex, 0, valueAtCurrent)
    }

    function addLoopToStem(stemIndex: number) {
      if (self.stems[stemIndex].type() === StemType.AUDIO) return
      ;(self.stems[stemIndex] as IInstrumentStemStore | IDrumStemStore).addLoop()
    }
    function updateLoopForStem(index: number, section: number, value: number) {
      if (self.stems[index].type() === StemType.AUDIO) return
      self.stems[index].updateLoopInStem(section, value)
    }
    function updateMelodicStemSample(index: number, sample: ISampleWithEffectsStore) {
      if (self.stems[index].isRemixed) {
        return
      }
      getStem(index).updateMelodicSample(sample)
    }
    function updateStemSampleStart(startTime: number, index: number) {
      getStem(index).updateSampleStart(startTime)
    }
    function updateStemSampleEnd(endTime: number, index: number) {
      getStem(index).updateSampleEnd(endTime)
    }
    function getStem(index: number): any {
      return self.stems[index] as any
    }
    function updateDrumStemSample(
      index: number,
      sample: ISampleWithEffectsStore,
      drumIndex: number
    ) {
      if (self.stems[index].isRemixed) {
        return
      }
      getStem(index).updateDrumSample(sample, drumIndex)
    }

    function updateDrumStemGain(index: number, drumIndex: number, gain: number) {
      getStem(index).updateDrumGain(drumIndex, gain)
    }

    function generateRandom(generator: SongGenerator) {
      generator.generateFullSong(self as any)
    }
    /** Populates this entire song from the given genesis song. */
    function fromGenesis(arp1: SongInternal) {
      self.tempo = arp1.getTempo()

      // old gain modifiers, now we don't use these, we just set manually.
      // so can go ahead and manually modify the gains for arp1s
      // const BASS_VOL: number = 1;
      const MELODY_VOL: number = 0.3
      const HARMONY_VOL: number = 0.3

      const leadStem = InstrumentStemStore.create()
      leadStem.fromGenesisMelodic(
        Arp2Utils.convertGenesisSampleWithEffectsToV2(
          GenesisSampleType.MELODY,
          arp1.getSelectedSamples().getMelody()
        ),
        arp1.getSongStructure().getMelodyFlow().getFlowList(),
        arp1.getMelodyA(),
        arp1.getMelodyB(),
        arp1.getMelodyC(),
        arp1.getInstrumentGain().getMelody() * MELODY_VOL,
        arp1.getMelodyOctave(),
        false,
        arp1.getMelodyA().getNotesList().length === 32
          ? BeatResolution.EIGHTH_NOTE
          : BeatResolution.SIXTEENTH_NOTE,
        arp1.getKey()
      )
      self.stems.push(leadStem)

      const harmonyStem = InstrumentStemStore.create()
      harmonyStem.fromGenesisMelodic(
        Arp2Utils.convertGenesisSampleWithEffectsToV2(
          GenesisSampleType.HARMONY,
          arp1.getSelectedSamples().getHarmony()
        ),
        arp1.getSongStructure().getHarmonyFlow().getFlowList(),
        arp1.getHarmonyA(),
        arp1.getHarmonyB(),
        arp1.getHarmonyC(),
        arp1.getInstrumentGain().getHarmony() * HARMONY_VOL,
        arp1.getHarmonyOctave(),
        false,
        BeatResolution.EIGHTH_NOTE,
        arp1.getKey()
      )
      self.stems.push(harmonyStem)

      const bassStem = InstrumentStemStore.create()
      bassStem.fromGenesisMelodic(
        Arp2Utils.convertGenesisSampleWithEffectsToV2(
          GenesisSampleType.BASS,
          arp1.getSelectedSamples().getBass()
        ),
        arp1.getSongStructure().getBassFlow().getFlowList(),
        arp1.getBassA(),
        arp1.getBassB(),
        arp1.getBassC(),
        arp1.getInstrumentGain().getBass(),
        arp1.getBassOctave(),
        false,
        BeatResolution.EIGHTH_NOTE,
        arp1.getKey()
      )
      self.stems.push(bassStem)

      const drumStem = DrumStemStore.create()
      drumStem.fromGenesisDrum(arp1)
      self.stems.push(drumStem)
    }

    function updateTempo(tempo: number) {
      const currentTempo = self.tempo
      self.tempo = tempo
      self.stems.forEach((stem) => {
        if (stem.type() !== StemType.AUDIO) return
        ;(stem as IAudioStemStore).clips.forEach((clip: IClipStore) =>
          clip.updateTempo(currentTempo, tempo)
        )
      })
    }

    function setSongName(songName: string) {
      self.name = songName
    }
    /*
     * Processes this song for storage in a database or localstorage.
     * Inverse of deserializeSongFromStorage()
     *
     * Latest Versions:
     * {
     *  version: 5-6,
     *  compressedSnapshot: (gzip compressed stringified snapshot, 8-bit number array)
     * }
     *
     * Previous Versions:
     * {
     *  version: 2-4,
     *  compressedSnapshot: (lzw compressed stringified snapshot, 12 bit number array)
     * }
     */
    function serializeSongForStorage(): ArpeggiCompositionV5 {
      return serializeSongSnapshot(getSnapshot(self))
    }
    /**
     * Removes unused parts of this song, saving storage space.
     * This removes:
     *  - stems with no loops
     *  - loops that don't appear in the arrangement view
     *  - loops that have no notes
     *  - noterows that are all 0 get replaced by "null"
     */
    function minimizeForStorage() {
      // Minimize each stem
      self.stems.forEach((stem) => {
        stem.minimizeForStorage()
      })

      // Remove empty stems
      for (let i = self.stems.length - 1; i >= 0; i--) {
        const stem = self.stems[i]
        if (stem.type() === StemType.AUDIO) {
          if ((stem as IAudioStemStore).clips.length === 0) removeStem(i)
        } else {
          if (stem.stemSettings.arrangement.every((loopIndex) => loopIndex === 0)) {
            removeStem(i)
          }
        }
      }
    }

    function setTokenColor(newColor: string) {
      self.tokenColor = newColor
      self.stems.forEach((stem) => stem.setTokenColor && stem.setTokenColor(newColor))
    }

    ///////////////////// Clips/Audio Stems ///////////////////////

    /**
     * Moves a clip from one stem to another
     * @param clipID the uniqueId of the clip to move
     * @param toStemIndex the index of the stem to move to clip to
     * @returns true or false based on whether it was successful or not
     */
    function moveClipToStem(clipID: string, toStemIndex: number): boolean {
      const fromStemIndex = self.getClipParentStemIndex(clipID)
      if (fromStemIndex === null || fromStemIndex === toStemIndex) return false
      const fromAudioStem = self.stems[fromStemIndex] as IAudioStemStore
      const clip = fromAudioStem.removeClipByID(clipID)

      const toAudioStem = self.stems[toStemIndex] as IAudioStemStore
      if (!clip || toAudioStem.type() !== StemType.AUDIO) {
        fromAudioStem.addClip(clip)
        return false
      }
      toAudioStem.addClip(clip)
      return true
    }

    /**
     * Gets the nearest audio stem index to the stem with index stemIndex, in either above (lower index) or below (higher index),
     * returns null if there are no audio stem neighbors in that direction
     * @param stemIndex index of the stem to get the neighbor of
     * @param direction the direction to check for the neighbor (above = lower index, below = higher index)
     */
    function getNeighboringAudioStemIndex(
      stemIndex: number,
      direction: 'above' | 'below'
    ): number | null {
      let index: number
      switch (direction) {
        case 'above':
          index = stemIndex - 1
          while (index >= 0) {
            if (self.stems[index].type() === StemType.AUDIO) return index
            index -= 1
          }
          return null
        case 'below':
          index = stemIndex + 1
          while (index < self.stems.length) {
            if (self.stems[index].type() === StemType.AUDIO) return index
            index += 1
          }
          return null
      }
    }

    /**
     * Transposes a clip up or down to match the key of the current song
     * @param clipID the ID of the clip you want to tune
     */
    function tuneClipToSong(clipID: string) {
      const clip = self.getClipByID(clipID)
      if (!clip) return

      const songKey: KeyAndScale = self.key
      if (songKey.key) {
        clip.updateKey(songKey.key)
      }
    }

    return {
      afterCreate,
      fromGenesis,
      updateStemSampleStart,
      updateStemSampleEnd,
      updateTempo,
      addInstrumentStem,
      addAudioStem,
      addDrumStem,
      addDrumSampleToStem,
      addStemFromRemix,
      removeStem,
      changeStemIndex,
      updateMelodicStemSample,
      updateDrumStemSample,
      updateDrumStemGain,
      setSongName,
      addLoopToStem,
      updateLoopForStem,
      generateRandom,
      serializeSongForStorage,
      minimizeForStorage,
      setTokenColor,
      moveClipToStem,
      getNeighboringAudioStemIndex,
      tuneClipToSong
    }
  })

// Register a pre-processor to handle migrating between versions.
const _SongStore = types.snapshotProcessor(SongStoreWithoutProcessor, {
  // Object -> Store (Deserialize)
  preProcessor(sn: any) {
    return migrateSongToLatest(sn)
  }
})

interface SongStoreType extends ForDirectExtend<typeof _SongStore> {}
export const SongStore: SongStoreType = _SongStore

/**
 * Creates a song from the serialized verison. Inverse of SongStore.serializeForStorage().
 */
export const deserializeSongFromStorage = (serializedSong: any) => {
  const parsedSong = deserializeSongSnapshot(serializedSong)

  // Then, apply the snapshot. This step auto-migrates to the latest version.
  return SongStore.create(parsedSong)
}

export interface ISongStore extends ForDirectExtend<Instance<typeof SongStore>> {}
export interface ISongStoreSnapshotIn extends SnapshotIn<typeof SongStore> {}
export interface ISongStoreSnapshotOut extends SnapshotOut<typeof SongStore> {}

const _GenesisSong = types.model({
  song: types.number,
  artist: types.string,
  title: types.string,
  metadata: types.model({
    address: types.string,
    animation_url: types.string,
    artist: types.string,
    attributes: types.array(
      types.model({
        trait_type: types.string,
        value: types.string
      })
    ),
    composition: types.string,
    description: types.string,
    external_url: types.string,
    image: types.string,
    mint_time: types.union(types.string, types.number),
    name: types.string,
    titles: types.string
  })
})

interface GenesisSongType extends ForDirectExtend<typeof _GenesisSong> {}
export const GenesisSong: GenesisSongType = _GenesisSong

export interface IGenesisSong extends ForDirectExtend<Instance<typeof GenesisSong>> {}
